import React, { useEffect, useState, useCallback } from "react";
import { Modal, Spinner, Badge, Form, ProgressBar } from "react-bootstrap";
import { FaPen, FaCheck, FaTimes, FaBriefcase, FaTasks, FaCalendarAlt } from "react-icons/fa";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";

import { 
  updateAllocation, 
  fetchTimeDistributionData, 
  updateWorkPackageMember, 
  fetchUserAllocations 
} from "../../../../redux/slices/timeDistributionSlice";

const PROJECT_TYPE_COLORS = {
  Financed: "success",
  Client: "info",
  Internal: "secondary",
};

const formatDate = (value, locale) => {
  if (!value) return "-";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "-";
  return new Intl.DateTimeFormat(locale || "pt-PT", { day: "2-digit", month: "short", year: "numeric" }).format(parsed);
};

const EditableETI = ({ value, onSave }) => {
  const { t } = useTranslation(); 
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [val, setVal] = useState(value);

  useEffect(() => { setVal(value); }, [value]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSave(parseFloat(val));
      setIsEditing(false);
    } catch (err) {
      console.error(t('components.userAllocationModal.errors.save_eti'), err);
      setVal(value);
    } finally {
      setIsSaving(false);
    }
  };

  if (isEditing) {
    return (
      <div className="d-flex align-items-center gap-1">
        <Form.Control
          type="number"
          step="0.05"
          min="0"
          max="1"
          value={val}
          onChange={(e) => setVal(e.target.value)}
          style={{ width: "70px", fontSize: "0.8rem" }}
          size="sm"
          disabled={isSaving}
          autoFocus
        />
        {isSaving ? (
          <Spinner animation="border" size="sm" className="ms-1" style={{ width: "12px", height: "12px", color: 'var(--brand-primary)' }} />
        ) : (
          <>
            <FaCheck role="button" className="text-success ms-1" onClick={handleSave} />
            <FaTimes role="button" className="text-danger" onClick={() => setIsEditing(false)} />
          </>
        )}
      </div>
    );
  }

  return (
    <Badge
      bg="light"
      text="dark"
      className="border px-2 py-1 d-inline-flex align-items-center gap-2 shadow-sm"
      role="button"
      onClick={() => setIsEditing(true)}
    >
      {Number(value).toFixed(2)} {t('components.userAllocationModal.eti_per_month')}
      <FaPen size={10} className="txt-subtle" />
    </Badge>
  );
};

const UserAllocationModal = ({ show, onHide, userId, userName, month }) => {
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async (showLoader = true) => {
    if (!userId) return;
    if (showLoader) setIsLoading(true);
    setError(null);
    try {
      const result = await dispatch(fetchUserAllocations({ userId, month })).unwrap();
      setData(result);
    } catch (err) {
      setError(err || t('components.userAllocationModal.errors.load_allocations'));
    } finally {
      setIsLoading(false);
    }
  }, [userId, month, dispatch, t]);

  useEffect(() => {
    if (show) fetchData();
  }, [show, fetchData]);

  const handleUpdateProjectETI = async (allocationId, newEti) => {
    await dispatch(updateAllocation({
      allocationId,
      data: { monthly_etis: newEti, month }
    })).unwrap();
    
    await fetchData(false);
    dispatch(fetchTimeDistributionData({ month }));
  };

  const handleUpdateWorkPackageETI = async (wpId, newEti) => {
    if (!wpId) throw new Error(t('components.userAllocationModal.errors.missing_wp_id'));

    await dispatch(updateWorkPackageMember({
      wpId,
      userId,
      etis: newEti,
      month,
    })).unwrap();
    
    await fetchData(false); 
    dispatch(fetchTimeDistributionData({ month }));
  };

  const totalEti = data?.allocations?.reduce((sum, a) => sum + a.monthly_etis, 0) || 0;
  const totalPercent = Math.min(totalEti * 100, 100);
  const financedCount = data?.allocations?.filter(a => a.is_financed).length || 0;
  const sortedAllocations = data?.allocations
    ? [...data.allocations].sort((a, b) => {
        if (a.is_financed && !b.is_financed) return -1;
        if (!a.is_financed && b.is_financed) return 1;
        return a.project_name.localeCompare(b.project_name, 'pt');
      })
    : [];

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton className="border-bottom-0 pb-0">
        <Modal.Title className="fw-bold fs-5 txt-main">
          {t('components.userAllocationModal.title', { userName })}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="pt-3">
        {isLoading && !data ? (
          <div className="text-center py-5">
            <Spinner animation="border" size="sm" style={{ color: 'var(--brand-primary)' }} />
          </div>
        ) : error ? (
          <div className="theme-alert-danger border-0">{error}</div>
        ) : data ? (
          <>
            <div className="p-3 bg-surface rounded-3 mb-4 border">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <div>
                  <h6 className="mb-0 fw-bold txt-main">{t('components.userAllocationModal.summary_title')}</h6>
                  <span className="txt-subtle" style={{ fontSize: "0.85rem" }}>
                    {data.allocations.length === 1 
                      ? t('components.userAllocationModal.project_count', { count: data.allocations.length }) 
                      : t('components.userAllocationModal.project_count_plural', { count: data.allocations.length })
                    }
                    {" · "}
                    {financedCount === 1 
                      ? t('components.userAllocationModal.financed_count', { count: financedCount })
                      : t('components.userAllocationModal.financed_count_plural', { count: financedCount })
                    }
                  </span>
                </div>
                <Badge bg={totalEti > 1.0 ? "danger" : "success"} className="px-3 py-2 fs-6" style={{ backgroundColor: totalEti > 1.0 ? 'var(--danger-red)' : 'var(--success-green)'}}>
                  {totalEti.toFixed(2)} / 1.00
                </Badge>
              </div>
              <ProgressBar
                now={totalPercent}
                variant={totalEti > 1.0 ? "danger" : "success"}
                style={{ height: "8px", backgroundColor: 'var(--gray-200)' }}
              />
            </div>

            {sortedAllocations.length === 0 ? (
              <div className="text-center py-4 txt-subtle">{t('components.userAllocationModal.no_allocations')}</div>
            ) : (
              sortedAllocations.map((allocation) => {
                const hasWorkPackages = allocation.work_packages && allocation.work_packages.length > 0;

                return (
                  <div key={allocation.allocation_id} className="border rounded-3 p-3 mb-3 bg-surface shadow-sm">
                    <div className="d-flex justify-content-between align-items-start mb-2">
                      <div className="d-flex align-items-center gap-2">
                        <FaBriefcase style={{ color: 'var(--brand-primary)' }} size={14} />
                        <div>
                          <h6 className="fw-bold mb-0 txt-main">{allocation.project_name}</h6>
                          <div className="d-flex align-items-center gap-2 mt-1">
                            <Badge bg={PROJECT_TYPE_COLORS[allocation.project_type] || "secondary"} className="fw-normal" style={{ fontSize: "0.7rem" }}>
                              {t(`components.userAllocationModal.project_types.${allocation.project_type}`, { defaultValue: allocation.project_type })}
                            </Badge>
                            <span className="txt-subtle fw-semibold" style={{ fontSize: "0.75rem" }}>
                              {allocation.project_status}
                            </span>
                          </div>
                        </div>
                      </div>

                      {hasWorkPackages ? (
                        <Badge bg="transparent" className="border border-secondary px-2 py-1 txt-main d-flex align-items-center gap-1 opacity-75" style={{ cursor: "default" }}>
                          <span className="fw-bold">{Number(allocation.monthly_etis).toFixed(2)}</span>
                          <span className="txt-subtle fw-normal" style={{ fontSize: "0.7rem" }}>{t('components.userAllocationModal.total_eti')}</span>
                        </Badge>
                      ) : (
                        <EditableETI
                          value={allocation.monthly_etis}
                          onSave={(newEti) => handleUpdateProjectETI(allocation.allocation_id, newEti)}
                        />
                      )}
                    </div>

                    {hasWorkPackages ? (
                      <div className="mt-3 ps-3 pt-2" style={{ borderLeft: "2px solid var(--gray-200)" }}>
                        <div className="d-flex align-items-center gap-2 mb-2 txt-subtle" style={{ fontSize: "0.75rem" }}>
                          <FaTasks size={11} />
                          <span className="text-uppercase fw-bold">
                            {t('components.userAllocationModal.work_packages_title', { count: allocation.work_packages.length })}
                          </span>
                        </div>
                        {allocation.work_packages.map((wp) => (
                          <div key={wp.id || wp.work_package_id} className="d-flex justify-content-between align-items-center py-2" style={{ borderBottom: "1px solid var(--gray-100)", fontSize: "0.85rem" }}>
                            <div>
                              <div className="fw-bold txt-main">{wp.name}</div>
                              <div className="txt-subtle d-flex align-items-center gap-1 mt-1" style={{ fontSize: "0.7rem" }}>
                                <FaCalendarAlt size={9} />
                                {formatDate(wp.start_date, i18n.language)} — {formatDate(wp.end_date, i18n.language)}
                              </div>
                            </div>
                            <EditableETI
                              value={wp.etis}
                              onSave={(newEti) => handleUpdateWorkPackageETI(wp.id || wp.work_package_id || wp.wp_id, newEti)}
                            />
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="txt-subtle mt-2 ps-3" style={{ fontSize: "0.8rem", fontStyle: "italic" }}>
                        {t('components.userAllocationModal.managed_directly')}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </>
        ) : null}
      </Modal.Body>
    </Modal>
  );
};

export default UserAllocationModal;
